import {Component, Input, Output, EventEmitter, ViewChild, ViewChildren, QueryList, OnInit} from '@angular/core';
import {FormGroup, AbstractControl} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {AppFileInputComponent} from 'src/app/shared/inputs/app-file-input/app-file-input.component';
import {DialogService} from 'src/app/shared/services/dialog.service';
import {UploadMessagesService} from 'src/app/shared/services/upload-messages.service';
import {PhotoCropComponent} from 'src/app/shared/ui/components/photo-crop/photo-crop.component';
import {DialogConfirmComponent} from '../dialogs/dialog-confirm/dialog-confirm.component';

@Component({
    selector: 'app-file-input-crop',
    templateUrl: './file-input-crop.component.html',
    styleUrls: ['./file-input-crop.component.scss'],
    providers: [UploadMessagesService],
    standalone: false
})
export class FileInputCropComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() controlName = 'photo';
  @Input() base64Control = null;
  @Input() previewName = 'photo_preview';
  @Input() fileTypes = ['.jpg', '.png'];
  @Input() showRemoveBtn = true;
  @Input() cropConf: {
    cropperMinWidth: number;
    cropperMinHeight: number;
    cropperMaxWidth: number;
    cropperMaxHeight: number;
    ratio?: string; // do not pass - calculated from max width height properties
    freeMaxCrop?: boolean; //if true allows crop area extend fully but keeping ratio
  };
  @Input() fileInputStyles: Record<string, string> = {};
  @Output() cropped = new EventEmitter<any>();
  @Output() fileUploading = new EventEmitter<any>();

  @ViewChildren(AppFileInputComponent) flow!: QueryList<AppFileInputComponent>;
  @ViewChild(AppFileInputComponent) fileInputComponent!: AppFileInputComponent;

  readonly defaultFileInputStyles: Record<string, string> = {
    '--afi-file-input-padding-left': '15px',
    '--afi-drop-area-margin-top': '0',
    '--afi-mobile-drop-area-width': '100%',
  };

  openPhotoCrop = false;
  blockCropping = false;
  photo!: string;
  cropperPosition: any;
  croppedResolution: any;
  private photoCropRef!: MatDialogRef<PhotoCropComponent>;

  constructor(
    private dialogService: DialogService,
    private uploadMessages: UploadMessagesService,
  ) {}

  ngOnInit(): void {
    if (this.cropConf.cropperMaxWidth && this.cropConf.cropperMaxHeight) {
      const ratio = String(this.cropConf.cropperMaxHeight / this.cropConf.cropperMaxWidth);
      this.cropConf.ratio = `1/${ratio}`;
    }
    if (this.cropConf?.freeMaxCrop) {
      this.cropConf.cropperMaxWidth = 100000;
      this.cropConf.cropperMaxHeight = 100000;
    }
  }

  callDialog = data => {
    const dialogRef = this.dialogService.openDialog(DialogConfirmComponent, {
      width: '670px',
      data: data,
      panelClass: ['cdk-mobile-full-screen', 'cdk-mobile-full-screen-vertical-center'],
    });
  };

  onFileSuccessfullyAddedImage(uploadedFileId: any, formControl: AbstractControl, formControlPreview: AbstractControl) {
    const errorMessages = this.uploadMessages.errorMessages;

    if (uploadedFileId in errorMessages) {
      formControl.setValue(null);
      formControlPreview.setValue(null);
      this.cropperPosition = null;
      const messageData = {
        title: 'Upload error',
        descriptionHtml: errorMessages[uploadedFileId],
        confirmBtn: 'OK',
      };
      this.callDialog(messageData);
      this.fileInputComponent.cancelAllFiles();
    } else {
      formControl.setValue(uploadedFileId);
      const params = {
        identifier: uploadedFileId,
        crop: this.cropperPosition,
        cropSize: this.croppedResolution ?? null,
      };
      this.cropped.emit(params);
    }
    this.fileUploading.emit(false);
  }

  onFileAdded(event: any, formControl?: AbstractControl) {
    const file = event[0][0].file;
    if (formControl) {
      formControl.markAllAsTouched();
    }
    if (formControl && file && file.type.startsWith('image/')) {
      if (!this.blockCropping) {
        this.cropThisPhoto(file);
      }
    }
  }

  cropThisPhoto(file?: File) {
    this.openPhotoCrop = true;
    this.fileUploading.emit(true);
    if (file) {
      this.openPhotoCropDialog(file, true);
    } else if (this.photo) {
      this.openPhotoCropDialog(this.photo, false);
    }
  }

  openPhotoCropDialog(imageURLorFile: any, imageFile?: boolean) {
    const data: any = {
      [imageFile ? 'imageFile' : 'imageURL']: imageURLorFile,
      ratio: this.cropConf.ratio,
      imgMedia: {width: 0, height: 0},
      cropperMinWidth: this.cropConf.cropperMinWidth,
      cropperMinHeight: this.cropConf.cropperMinHeight,
      cropperMaxWidth: this.cropConf.cropperMaxWidth,
      cropperMaxHeight: this.cropConf.cropperMaxHeight,
      ...(this.cropConf.freeMaxCrop !== undefined && {freeMaxCrop: this.cropConf.freeMaxCrop}),
    };

    this.photoCropRef = this.dialogService.openDialog<any>(PhotoCropComponent, {
      width: '1220px',
      data: data,
    });

    this.photoCropRef.afterClosed().subscribe((data: any) => {
      this.openPhotoCrop = false;
      this.cropperPosition = data?.cropper;
      this.croppedResolution = data?.croppedImageResolution;

      if (!data && !this.photo) {
        this.fileInputComponent.cancelAllFiles();
        this.fileUploading.emit(false);
      }
      if (data?.saved) {
        this.handleCroppedFile(data.croppedImageFile, data.croppedImage);
        this.photo = data.croppedImageFile;
      } else {
        this.fileUploading.emit(false);
      }
    });
  }

  handleCroppedFile(croppedFile: File, croppedDataUrl: string) {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      this.form.get(this.previewName)!.setValue(croppedDataUrl);
      this.form.get(this.controlName)!.setValue(null);
    };
    reader.readAsDataURL(croppedFile);

    this.blockCropping = true;
    setTimeout(() => (this.blockCropping = false), 2500);

    this.fileInputComponent.flow.cancel();
    this.fileInputComponent.flow.addFile(croppedFile);
    this.fileInputComponent.uploadFlow();

    this.photoCropRef.close();
  }

  fileRemoved(event: any, formControl: AbstractControl, formControlPreview?: AbstractControl) {
    if (event && this.showRemoveBtn) {
      formControl.setValue('');
      if (formControlPreview) {
        formControlPreview.setValue('');
      }
    }
  }

  cropFromBase() {
    if (this.base64Control) {
      const base64 = this.form.get(this.base64Control).value;
      const file = this.base64ToFile(base64, 'cropped-file');
      this.openPhotoCropDialog(file, true);
    }
  }

  private base64ToFile(base64: string, fileName: string, mime = 'image/png'): File {
    const byteString = atob(base64);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([uint8Array], {type: mime});
    return new File([blob], fileName, {type: mime});
  }

  get mergedFileInputStyles(): Record<string, string> {
    return {
      ...this.defaultFileInputStyles,
      ...this.fileInputStyles,
    };
  }
}
